/**
 * Sentry Integration Utility
 * 
 * Handles error reporting to Sentry with privacy considerations
 */

import type { AppError } from './types';

// Types for Sentry integration (to avoid requiring @sentry/* packages in shared)
interface SentryEvent {
  level?: 'error' | 'warning' | 'info';
  tags?: Record<string, string>;
  contexts?: Record<string, any>;
  extra?: Record<string, any>;
  fingerprint?: string[];
}

interface SentryBreadcrumb {
  message: string;
  category?: string;
  data?: any;
  level?: string;
}

// Interface for Sentry SDK (injected at runtime)
interface SentrySDK {
  captureException: (error: Error, options?: SentryEvent) => string;
  captureMessage: (message: string, options?: SentryEvent) => string;
  addBreadcrumb: (breadcrumb: SentryBreadcrumb) => void;
}

export class SentryErrorReporter {
  private static sdk: SentrySDK | null = null;
  private static isInitialized = false;

  /**
   * Initialize with Sentry SDK instance
   */
  static initialize(sentrySDK: SentrySDK) {
    this.sdk = sentrySDK;
    this.isInitialized = true;
  }

  /**
   * Check if Sentry is available and initialized
   */
  static isAvailable(): boolean {
    return this.isInitialized && this.sdk !== null;
  }

  /**
   * Report an AppError to Sentry
   */
  static captureError(error: AppError): string | null {
    if (!this.isAvailable() || !error.shouldReportToSentry) {
      return null;
    }

    try {
      const sentryEvent: SentryEvent = {
        level: error.sentryLevel,
        tags: {
          errorCode: error.code,
          errorCategory: error.category,
          correlationId: error.correlationId || 'unknown'
        },
        contexts: {
          error: {
            code: error.code,
            category: error.category,
            retryable: error.retryable,
            status: error.status,
            timestamp: error.timestamp
          }
        },
        extra: {
          context: this.sanitizeContext(error.context),
          fieldErrors: error.fieldErrors,
          userActions: error.userActions?.map(action => ({
            type: action.type,
            label: action.label,
            url: action.url
          }))
        },
        fingerprint: [error.code, error.category]
      };

      return this.sdk!.captureException(new Error(error.message), sentryEvent);
    } catch (sentryError) {
      console.warn('Failed to report error to Sentry:', sentryError);
      return null;
    }
  }

  /**
   * Capture a simple message
   */
  static captureMessage(
    message: string, 
    level: 'error' | 'warning' | 'info' = 'info', 
    context?: any
  ): string | null {
    if (!this.isAvailable()) {
      return null;
    }

    try {
      return this.sdk!.captureMessage(message, {
        level,
        extra: this.sanitizeContext(context)
      });
    } catch (sentryError) {
      console.warn('Failed to capture message in Sentry:', sentryError);
      return null;
    }
  }

  /**
   * Add breadcrumb for debugging context
   */
  static addBreadcrumb(message: string, category?: string, data?: any): void {
    if (!this.isAvailable()) {
      return;
    }

    try {
      this.sdk!.addBreadcrumb({
        message,
        category: category || 'error',
        data: this.sanitizeContext(data),
        level: 'info'
      });
    } catch (sentryError) {
      console.warn('Failed to add breadcrumb to Sentry:', sentryError);
    }
  }

  /**
   * Sanitize context data to remove sensitive information
   */
  private static sanitizeContext(context: any): any {
    if (!context || typeof context !== 'object') {
      return context;
    }

    const sanitized = { ...context };
    
    // List of sensitive keys to remove
    const sensitiveKeys = [
      'password',
      'token',
      'secret',
      'key',
      'auth',
      'session',
      'cookie',
      'credential',
      'partnershipDetails',
      'habitContent', 
      'chatMessages',
      'privateMessages',
      'sensitiveDetails',
      'userContent',
      'personalData'
    ];

    function sanitizeObject(obj: any): any {
      if (!obj || typeof obj !== 'object') {
        return obj;
      }

      if (Array.isArray(obj)) {
        return obj.map(item => sanitizeObject(item));
      }

      const result: any = {};
      for (const [key, value] of Object.entries(obj)) {
        const lowerKey = key.toLowerCase();
        
        // Remove sensitive keys
        if (sensitiveKeys.some(sensitiveKey => lowerKey.includes(sensitiveKey))) {
          result[key] = '[REDACTED]';
        } else if (typeof value === 'object' && value !== null) {
          result[key] = sanitizeObject(value);
        } else {
          result[key] = value;
        }
      }
      return result;
    }

    return sanitizeObject(sanitized);
  }

  /**
   * Create privacy-focused Sentry configuration
   */
  static createPrivacyConfig(): any {
    return {
      beforeSend: (event: any) => {
        // Remove sensitive data from error reports
        if (event.extra) {
          event.extra = this.sanitizeContext(event.extra);
        }
        
        if (event.contexts) {
          event.contexts = this.sanitizeContext(event.contexts);
        }

        // Remove sensitive tags
        if (event.tags) {
          const sanitizedTags: any = {};
          for (const [key, value] of Object.entries(event.tags)) {
            if (!key.toLowerCase().includes('sensitive')) {
              sanitizedTags[key] = value;
            }
          }
          event.tags = sanitizedTags;
        }

        return event;
      },
      
      beforeBreadcrumb: (breadcrumb: any) => {
        // Filter sensitive breadcrumbs
        if (breadcrumb.message && typeof breadcrumb.message === 'string') {
          const message = breadcrumb.message.toLowerCase();
          if (message.includes('password') || 
              message.includes('token') ||
              message.includes('sensitive')) {
            return null;
          }
        }

        // Sanitize breadcrumb data
        if (breadcrumb.data) {
          breadcrumb.data = this.sanitizeContext(breadcrumb.data);
        }

        return breadcrumb;
      }
    };
  }
}